import React from 'react';
import { RefreshCw, Save, Printer, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

/**
 * SyncHeader Component
 * Reusable header for ICS forms with real-time sync capabilities
 */
const SyncHeader = ({
  title,
  description,
  lastSaved,
  hasUnsavedChanges,
  loading,
  onSave,
  onRefresh,
  onPrint,
  showPrint = true,
  saveLabel = 'Save All',
  printLabel = 'Print',
  className = ''
}) => {
  return (
    <div className={`flex justify-between items-start mb-8 ${className}`}>
      <div className="text-center flex-1">
        <h1 className="text-3xl font-bold text-white mb-2">{title}</h1>
        {description && (
          <p className="text-gray-400 text-lg">{description}</p>
        )}
        <div className="flex items-center justify-center gap-4 mt-2">
          {lastSaved && (
            <p className="text-xs text-gray-500">
              Last saved: {new Date(lastSaved).toLocaleString()}
            </p>
          )}
          {hasUnsavedChanges && (
            <span className="text-xs text-yellow-500 font-semibold px-2 py-1 bg-yellow-500/10 rounded flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Unsaved changes
            </span>
          )}
        </div>
        <p className="text-xs text-gray-600 mt-1">
          Auto-syncs every 15 seconds • Click Refresh to sync manually
        </p>
      </div>
      <div className="flex gap-2">
        <Button 
          onClick={onRefresh}
          disabled={loading}
          variant="outline"
          className="border-gray-500 text-gray-400 hover:bg-gray-700"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <Button 
          onClick={onSave}
          disabled={loading}
          className={`${hasUnsavedChanges ? 'bg-orange-600 hover:bg-orange-700' : 'bg-orange-500 hover:bg-orange-600'} text-white`}
        >
          <Save className="h-4 w-4 mr-2" />
          {loading ? 'Saving...' : saveLabel}
        </Button>
        {showPrint && onPrint && (
          <Button 
            onClick={onPrint}
            className="bg-blue-500 hover:bg-blue-600 text-white"
          >
            <Printer className="h-4 w-4 mr-2" />
            {printLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * CardSaveButton Component
 * Reusable Save button for card headers
 */
export const CardSaveButton = ({ 
  onSave, 
  loading, 
  hasUnsavedChanges,
  size = 'sm' 
}) => {
  return (
    <Button 
      onClick={onSave}
      disabled={loading}
      className={`${hasUnsavedChanges ? 'bg-orange-600 hover:bg-orange-700' : 'bg-orange-500 hover:bg-orange-600'} text-white`}
      size={size}
    >
      <Save className="h-4 w-4 mr-2" />
      {loading ? 'Saving...' : 'Save'}
    </Button>
  );
};

/**
 * RefreshButton Component
 * Standalone refresh button
 */
export const RefreshButton = ({ 
  onRefresh, 
  loading,
  size = 'sm' 
}) => {
  return (
    <Button 
      onClick={onRefresh}
      disabled={loading}
      variant="outline"
      className="border-gray-500 text-gray-400 hover:bg-gray-700"
      size={size}
    >
      <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
      Refresh
    </Button>
  );
};

export default SyncHeader;
